import { useEffect } from "react";
import { useLocation } from "../hooks/useRouter";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { fetchNpsEligibility, fetchModelList } from "../store/apiActions";
import { setSelectedAgent, setNpsPopupVisibility } from "../store/actions";
import { RBAC_APPS, UplGenieFeatures } from "../constant/rbacConfig";
import ProductivityFeedbackPopup from "../components/ProductivityFeedback/ProductivityFeedback";
import { useAuth } from "../hooks/useAuth";
import { useRbac } from "../hooks/useRbac";
import { STORAGE_KEYS, ROUTES } from "../constant/keyConstants";
import { useEventEmitter } from "../contexts/EventEmmiter";
import { useConstants } from "../constant/constantProvider";

const GlobalMiddleware = ({ children }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { isLoggedIn } = useAuth();
  const { subscribe } = useEventEmitter();
  const showNpsPopup = useSelector((state) => state?.state?.showNpsPopup);
  const selectedAgent = useSelector((state) => state?.state?.selectedAgent);
  const { uplGenieEnabledFeatures, isRbacLoaded } = useRbac();
  const { LOGIN_SUCCESS_MESSAGE } = useConstants();

  // Show login success toast once after SSO redirect, then clear the flag
  useEffect(() => {
    const isLogin = localStorage.getItem(STORAGE_KEYS.IS_LOGIN) === "true";
    if (isLogin) {
      toast.info(LOGIN_SUCCESS_MESSAGE);
      localStorage.removeItem(STORAGE_KEYS.IS_LOGIN);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 1. Set Selected Agent based on Route
  useEffect(() => {
    const path = location.pathname;

    // App routes → set the matching agent
    if (path.startsWith(ROUTES.GMT_CHAT)) {
      dispatch(setSelectedAgent(RBAC_APPS.GMT));
    } else if (path.startsWith(ROUTES.PROCUREMENT_AI)) {
      dispatch(setSelectedAgent(RBAC_APPS.PROCUREMENT));
    } else if (path.startsWith(ROUTES.TRANSLATE)) {
      dispatch(setSelectedAgent(RBAC_APPS.TRANSLATION));
    } else if (path === ROUTES.CHAT || path.startsWith(ROUTES.CHAT + "/")) {
      dispatch(setSelectedAgent(RBAC_APPS.CHAT_GENIE));
    } else if (path.startsWith(ROUTES.DEEP_INSIGHTS_CHAT)) {
      dispatch(setSelectedAgent(RBAC_APPS.DEEP_INSIGHTS));
    } else if (path.startsWith(ROUTES.TALENT_PULSE_360)) {
      dispatch(setSelectedAgent(RBAC_APPS.TALENT_PULSE_360));
    } else {
      // Non-app pages (my-apps, decrypt, video-tutorials, user-management, etc.)
      // Set to null so app-specific API calls (e.g. /models/base) are NOT triggered.
      dispatch(setSelectedAgent(null));
    }
  }, [location.pathname, dispatch]);

  useEffect(() => {
    if (!isLoggedIn()) return;

    const checkNps = async () => {
      // Only check if Chat Genie is active
      const path = location.pathname;
      if (!(path === "/chat" || path.startsWith("/chat/"))) return;

      const today = new Date().toISOString().split('T')[0];
      const lastCheckDate = localStorage.getItem(STORAGE_KEYS.LAST_NPS_CHECK_DATE);

      if (lastCheckDate === today) return;

      try {
        const res = await dispatch(fetchNpsEligibility());
        const data = res?.payload || res;

        localStorage.setItem(STORAGE_KEYS.LAST_NPS_CHECK_DATE, today);

        if (data?.show_nps === true) {
          dispatch(setNpsPopupVisibility(true));
        }
      } catch (error) {
        console.error("NPS Check failed:", error);
      }
    };

    const unsubscribe = subscribe("querySent", checkNps);

    return () => unsubscribe();
  }, [dispatch, isLoggedIn, subscribe, location.pathname]);
  
  // 3. Fetch Model List for Chat Genie (Migrated from Header)
  useEffect(() => {
    // Only fetch when explicitly on Chat Genie — selectedAgent === null means
    // we are on a non-app page (my-apps, decrypt, etc.) and must NOT call the API.
    if (!isLoggedIn() || !isRbacLoaded || selectedAgent !== RBAC_APPS.CHAT_GENIE) return;

    const isAdvancedModelEnabled = uplGenieEnabledFeatures?.[UplGenieFeatures.ADVANCED_MODAL] === true;
    const queryParams = isAdvancedModelEnabled ? { advance_model: true } : null;

    dispatch(fetchModelList(queryParams));
  }, [dispatch, isLoggedIn, selectedAgent, uplGenieEnabledFeatures, isRbacLoaded]);

  return (
    <>
      {children}
      {showNpsPopup && (
        <ProductivityFeedbackPopup
          onClose={() => dispatch(setNpsPopupVisibility(false))}
        />
      )}
    </>
  );
};

export default GlobalMiddleware;
